import React from 'react';
import axios from 'axios';
import { Button, Card, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { SystemApi } from '../../../services/Api';

const SHOW_COL = ['PID', '%CPU', '%MEM', 'TIME', 'COMMAND'];

interface PanelCpuProps {
    trans: (key: string) => string;
}

interface CpuRow {
    key: React.Key;
    values: string[];
}

interface OverviewRow {
    key: string;
    val: unknown;
}

interface PanelCpuState {
    list: CpuRow[];
    overview: OverviewRow[];
    loading: boolean;
}

export default class PanelCpu extends React.Component<PanelCpuProps, PanelCpuState> {
    state: PanelCpuState = {
        list: [],
        overview: [],
        loading: false,
    };

    updateInfo = (info: any) => {
        const tableHead: string[] = Array.isArray(info?.tableHead) ? info.tableHead : [];
        const tableData: unknown[][] = Array.isArray(info?.tableData) ? info.tableData : [];

        const colIdxList = SHOW_COL.map((name) => tableHead.indexOf(name));

        const list: CpuRow[] = tableData.map((data, rowIdx) => ({
            key: rowIdx,
            values: colIdxList.map((idx) => (idx >= 0 ? String(data[idx] ?? '') : '')),
        }));

        const overview: OverviewRow[] = Object.entries(info?.overview || {}).map(([key, val]) => ({
            key,
            val,
        }));

        this.setState({ list, overview });
    };

    refreshCpuInfo = async () => {
        const lang = localStorage.getItem('lang') || 'zh_CN';
        this.setState({ loading: true });

        try {
            const response = await axios.post(SystemApi.GET_CPU_STATUS, {
                lang: lang.split('_')[0],
            });

            if (response.data?.data) {
                this.updateInfo(response.data.data);
            }
        } catch (error) {
            console.error('PanelCpu.refreshCpuInfo()', error);
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { trans: t } = this.props;
        const { list, overview, loading } = this.state;

        const overviewColumns: ColumnsType<OverviewRow> = [
            {
                title: t('cpuOverview'),
                dataIndex: 'key',
                key: 'key',
                render: (key: string) => t(key),
            },
            {
                title: '',
                dataIndex: 'val',
                key: 'val',
                render: (val: unknown) => `${val}%`,
            },
        ];

        const columns: ColumnsType<CpuRow> = SHOW_COL.map((name, index) => ({
            title: t(name),
            key: name,
            render: (_value, record) => record.values[index],
        }));

        return (
            <Card
                title={t('cpuMgmt')}
                extra={
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={this.refreshCpuInfo}
                    >
                        {t('refresh')}
                    </Button>
                }
            >
                <Table<OverviewRow>
                    rowKey="key"
                    size="small"
                    bordered
                    pagination={false}
                    columns={overviewColumns}
                    dataSource={overview}
                />

                <Typography.Title level={5} style={{ marginTop: 24 }}>
                    {t('cpuInfo')}
                </Typography.Title>

                <Table<CpuRow>
                    rowKey="key"
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={list}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        );
    }
}
